/**
 * Import Dialog Component
 *
 * Dialog for importing IDF files with drag-and-drop support
 * and validation feedback. Includes version upgrade support
 * for older EnergyPlus files.
 */

import { useCallback, useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { useFileStore, formatFileSize } from '../index';
import type { ImportResult } from '../types';
import {
  canTransition,
  getTransitionChain,
  chainTransitions,
  getLatestTargetVersion,
} from '../../transition';
import { parseIDF, serializeIDF } from '../../parser';

interface ImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImportDialog({ open, onOpenChange }: ImportDialogProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [upgradeResult, setUpgradeResult] = useState<{
    success: boolean;
    newContent: string;
    newVersion: string;
    changesCount: number;
    warnings: string[];
  } | null>(null);
  const { importFile, confirmImport, importResult, isLoading } = useFileStore();

  // Check if the file can be upgraded
  const upgradeInfo = useMemo(() => {
    if (!importResult?.version) return null;

    const latestVersion = getLatestTargetVersion();
    const currentVersion = importResult.version;

    // Check if upgrade is needed and possible
    if (currentVersion === latestVersion) return null;
    if (!canTransition(currentVersion, latestVersion)) return null;

    return {
      currentVersion,
      targetVersion: latestVersion,
    };
  }, [importResult?.version]);

  // Handle upgrade action
  const handleUpgrade = useCallback(() => {
    if (!importResult?.content || !upgradeInfo) return;

    setIsUpgrading(true);
    try {
      // Parse the IDF content
      const parseResult = parseIDF(importResult.content);
      if (!parseResult.success) {
        throw new Error('Failed to parse IDF for upgrade');
      }

      // Get the transition chain and apply it
      const transitions = getTransitionChain(
        upgradeInfo.currentVersion,
        upgradeInfo.targetVersion
      );

      const transitionResult = chainTransitions(parseResult.document, transitions);

      if (!transitionResult.success) {
        throw new Error('Transition failed');
      }

      // Serialize the upgraded document
      const newContent = serializeIDF(transitionResult.document);

      setUpgradeResult({
        success: true,
        newContent,
        newVersion: upgradeInfo.targetVersion,
        changesCount: transitionResult.changes.length,
        warnings: transitionResult.warnings.map(w => w.message),
      });
    } catch (error) {
      console.error('Upgrade failed:', error);
      setUpgradeResult({
        success: false,
        newContent: '',
        newVersion: '',
        changesCount: 0,
        warnings: [error instanceof Error ? error.message : 'Unknown error'],
      });
    } finally {
      setIsUpgrading(false);
    }
  }, [importResult?.content, upgradeInfo]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (file && (file.name.endsWith('.idf') || file.name.endsWith('.imf'))) {
          await importFile(file);
        }
      }
    },
    [importFile]
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file) {
          await importFile(file);
        }
      }
    },
    [importFile]
  );

  const handleConfirm = useCallback(() => {
    if (importResult?.filename) {
      // Use upgraded content if available, otherwise use original
      const content = upgradeResult?.success
        ? upgradeResult.newContent
        : importResult.content;

      if (content) {
        confirmImport(content, importResult.filename);
        onOpenChange(false);
        // Reset upgrade state
        setUpgradeResult(null);
      }
    }
  }, [importResult, upgradeResult, confirmImport, onOpenChange]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
    // Reset upgrade state
    setUpgradeResult(null);
  }, [onOpenChange]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
        <div className="flex items-center justify-between border-b pb-4">
          <h2 className="text-lg font-semibold">Import IDF File</h2>
          <button
            onClick={handleCancel}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="py-4">
          {!importResult ? (
            <DropZone
              isDragging={isDragging}
              isLoading={isLoading}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onFileSelect={handleFileSelect}
            />
          ) : (
            <ImportResultDisplay
              result={importResult}
              upgradeInfo={upgradeInfo}
              upgradeResult={upgradeResult}
              isUpgrading={isUpgrading}
              onUpgrade={handleUpgrade}
            />
          )}
        </div>

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          {importResult && importResult.canProceed && (
            <Button onClick={handleConfirm}>
              {upgradeResult?.success
                ? `Import (v${upgradeResult.newVersion})`
                : importResult.errors.length > 0
                  ? 'Import Anyway'
                  : 'Import'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

interface DropZoneProps {
  isDragging: boolean;
  isLoading: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function DropZone({
  isDragging,
  isLoading,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileSelect,
}: DropZoneProps) {
  return (
    <div
      className={`flex min-h-[200px] flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
        isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
      }`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {isLoading ? (
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Reading file...</p>
        </div>
      ) : (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mb-4 text-muted-foreground"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <p className="mb-2 text-center text-sm text-muted-foreground">
            Drop IDF file here or click to browse
          </p>
          <label>
            <input
              type="file"
              accept=".idf,.imf"
              className="hidden"
              onChange={onFileSelect}
            />
            <span className="cursor-pointer text-sm font-medium text-primary hover:underline">
              Select File
            </span>
          </label>
        </>
      )}
    </div>
  );
}

interface ImportResultDisplayProps {
  result: ImportResult;
  upgradeInfo: {
    currentVersion: string;
    targetVersion: string;
  } | null;
  upgradeResult: {
    success: boolean;
    newContent: string;
    newVersion: string;
    changesCount: number;
    warnings: string[];
  } | null;
  isUpgrading: boolean;
  onUpgrade: () => void;
}

function ImportResultDisplay({
  result,
  upgradeInfo,
  upgradeResult,
  isUpgrading,
  onUpgrade,
}: ImportResultDisplayProps) {
  return (
    <div className="space-y-4">
      {/* File info */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded bg-muted">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        </div>
        <div>
          <p className="font-medium">{result.filename}</p>
          {result.fileSize && (
            <p className="text-sm text-muted-foreground">
              {formatFileSize(result.fileSize)}
            </p>
          )}
        </div>
      </div>

      {/* Parse status */}
      <div className="rounded-lg border p-4">
        {result.success ? (
          <div className="flex items-center gap-2 text-green-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span className="font-medium">Parsed successfully</span>
          </div>
        ) : result.canProceed ? (
          <div className="flex items-center gap-2 text-yellow-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span className="font-medium">Parsed with warnings</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-red-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            <span className="font-medium">Failed to parse</span>
          </div>
        )}

        <div className="mt-2 space-y-1 text-sm text-muted-foreground">
          {result.objectCount !== undefined && (
            <p>{result.objectCount} objects found</p>
          )}
          {result.version && (
            <p>
              EnergyPlus version: {result.version}
              {upgradeResult?.success && (
                <span className="ml-2 text-green-600">
                  → {upgradeResult.newVersion}
                </span>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Version Upgrade Banner */}
      {upgradeInfo && !upgradeResult && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950">
          <div className="flex items-start gap-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mt-0.5 flex-shrink-0 text-blue-600 dark:text-blue-400"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="16 12 12 8 8 12" />
              <line x1="12" y1="16" x2="12" y2="8" />
            </svg>
            <div className="flex-1">
              <h4 className="font-medium text-blue-700 dark:text-blue-300">
                Older EnergyPlus Version Detected
              </h4>
              <p className="mt-1 text-sm text-blue-600 dark:text-blue-400">
                This file uses EnergyPlus v{upgradeInfo.currentVersion}. Would you like to
                upgrade it to v{upgradeInfo.targetVersion}?
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900"
                onClick={onUpgrade}
                disabled={isUpgrading}
              >
                {isUpgrading ? (
                  <>
                    <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                    Upgrading...
                  </>
                ) : (
                  `Upgrade to v${upgradeInfo.targetVersion}`
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Success */}
      {upgradeResult?.success && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950">
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span className="font-medium">
              Upgraded to v{upgradeResult.newVersion}
            </span>
          </div>
          <p className="mt-2 text-sm text-green-600 dark:text-green-400">
            {upgradeResult.changesCount} change(s) applied during upgrade.
          </p>
          {upgradeResult.warnings.length > 0 && (
            <div className="mt-2 text-sm text-yellow-600 dark:text-yellow-400">
              <p className="font-medium">Upgrade warnings:</p>
              <ul className="mt-1 list-inside list-disc">
                {upgradeResult.warnings.slice(0, 3).map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
                {upgradeResult.warnings.length > 3 && (
                  <li>... and {upgradeResult.warnings.length - 3} more</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Upgrade Failed */}
      {upgradeResult && !upgradeResult.success && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            <span className="font-medium">Upgrade failed</span>
          </div>
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">
            {upgradeResult.warnings[0] || 'An unknown error occurred during upgrade.'}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            You can still import the original file without upgrading.
          </p>
        </div>
      )}

      {/* Errors */}
      {result.errors.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <h4 className="mb-2 flex items-center gap-2 font-medium text-red-700">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            {result.errors.length} error(s) found
          </h4>
          <ul className="space-y-1 text-sm text-red-600">
            {result.errors.slice(0, 5).map((error, i) => (
              <li key={i}>
                {error.line && <span className="font-mono">Line {error.line}: </span>}
                {error.message}
              </li>
            ))}
            {result.errors.length > 5 && (
              <li className="text-red-500">
                ... and {result.errors.length - 5} more errors
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Warnings */}
      {result.warnings.length > 0 && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <h4 className="mb-2 flex items-center gap-2 font-medium text-yellow-700">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            {result.warnings.length} warning(s) found
          </h4>
          <ul className="space-y-1 text-sm text-yellow-600">
            {result.warnings.slice(0, 5).map((warning, i) => (
              <li key={i}>
                {warning.line && <span className="font-mono">Line {warning.line}: </span>}
                {warning.message}
              </li>
            ))}
            {result.warnings.length > 5 && (
              <li className="text-yellow-500">
                ... and {result.warnings.length - 5} more warnings
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
